import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import EventFinished from "src/components/EventFinished";
import TitleSection from "src/components/TitleSection/TitleSection";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import { useWindowWidth } from "src/utils/windowUtils";

const FightQuest61 = () => {
  const { t, i18n } = useTranslation();
  const windowWidth = useWindowWidth();

  // Determine if mobile screen
  const isMobile = windowWidth < 992;

  // Select appropriate images based on screen size
  const heroImagePath = isMobile
    ? "entertainment/fightquest-61/FightQuest61-Website-LP-hero-mobile.jpg"
    : "entertainment/fightquest-61/FightQuest61-Website-LP-hero.jpg";

  const mainCardImage = isMobile
    ? `entertainment/fightquest-61/FightQuest61-Website-LP-main-card-${i18n.language}-mobile.jpg`
    : `entertainment/fightquest-61/FightQuest61-Website-LP-main-card-${i18n.language}.png`;

  const secondaryCardImage = isMobile
    ? `entertainment/fightquest-61/FightQuest61-Website-LP-secondary-card-${i18n.language}-mobile.jpg`
    : `entertainment/fightquest-61/FightQuest61-Website-LP-secondary-card-${i18n.language}.png`;

  const heroBgImage = useResponsiveImageUrl(heroImagePath);

  return (
    <MainDiv bgImage={heroBgImage}>
      {/* Full Height Hero Background */}
      <HeroSection />

      <EventFinished scheduleKeyName="fightQuest61" eventType="entertainment" />

      {/* Main Content - Title Section and Cards */}
      <container-div>
        {/* Title Section */}
        <TitleSection
          title={t("fightQuest61.content.title")}
          text={t("fightQuest61.content.subtitle")}
          borderColor="none"
          dark={false}
        />

        {/* Main Card - Fighters */}
        <MainCardSection>
          <ResponsiveImage
            src={mainCardImage}
            alt="FightQuest 61 Main Card"
          />
        </MainCardSection>

        {/* Secondary Card - Fighters */}
        <MainCardSection>
          <ResponsiveImage
            src={secondaryCardImage}
            alt="FightQuest 61 Secondary Card"
          />
        </MainCardSection>

        {/* Event Details, Ticket Pricing, and Image - 3 Columns */}
        <BottomSection>
          <Row className="m-0 bottom-row">
            {/* Event Details */}
            <Col xs={12} lg={4} className="p-0">
              <EventDetailsCard>
                <h2>{t("fightQuest61.eventDetails.title")}</h2>
                <DetailItem>
                  <ResponsiveImage
                    className="icon"
                    src="entertainment/fightquest-61/calendar.svg"
                    alt="Date"
                  />
                  <div>
                    <p className="label">{t("fightQuest61.eventDetails.dateLabel")}</p>
                    <p className="value">{t("fightQuest61.eventDetails.date")}</p>
                  </div>
                </DetailItem>
                <DetailItem>
                  <ResponsiveImage
                    className="icon"
                    src="entertainment/fightquest-61/door.svg"
                    alt="Doors Open"
                  />
                  <div>
                    <p className="label">{t("fightQuest61.eventDetails.doorsLabel")}</p>
                    <p className="value">{t("fightQuest61.eventDetails.doorsTime")}</p>
                  </div>
                </DetailItem>
                <DetailItem>
                  <ResponsiveImage
                    className="icon"
                    src="entertainment/fightquest-61/clock.svg"
                    alt="Fights Start"
                  />
                  <div>
                    <p className="label">{t("fightQuest61.eventDetails.fightsLabel")}</p>
                    <p className="value">{t("fightQuest61.eventDetails.fightsTime")}</p>
                  </div>
                </DetailItem>
              </EventDetailsCard>
            </Col>

            {/* Ticket Pricing */}
            <Col xs={12} lg={4} className="p-0">
              <TicketPricingCard>
                <h2>{t("fightQuest61.ticketPricing.title")}</h2>
                <PricingRow>
                  <div>
                    <p className="label">{t("fightQuest61.ticketPricing.presaleLabel")}</p>
                    <p className="price">{t("fightQuest61.ticketPricing.presalePrice")}</p>
                  </div>
                  <div className="separator" />
                  <div>
                    <p className="label">{t("fightQuest61.ticketPricing.doorLabel")}</p>
                    <p className="price">{t("fightQuest61.ticketPricing.doorPrice")}</p>
                  </div>
                </PricingRow>
                <p className="note">
                  <ReactMarkdown>
                    {t("fightQuest61.ticketPricing.note")}
                  </ReactMarkdown>
                </p>
              </TicketPricingCard>
            </Col>

            {/* Image */}
            <Col xs={12} lg={4} className="p-0">
              <ImageWrapper>
                <ResponsiveImage
                  src="entertainment/fightquest-61/fq-61-fight-ring.jpg"
                  alt="Fight Ring"
                />
              </ImageWrapper>
            </Col>
          </Row>
        </BottomSection>
      </container-div>
    </MainDiv>
  );
};

// Styled Components
const MainDiv = styled.div`
  width: 100%;
  background-image: ${(props) => `url(${props.bgImage})`};
  background-size: contain;
  background-position: top;
  background-repeat: no-repeat;
  color: var(--white);
  text-align: center;
  padding-bottom: 2rem;
  .title-section {
    span {
      display: none;
    }
    h1 {
      margin-bottom: 0;
      max-width: 500px;
    }
    h3 {
      color: var(--red);
    }
  }
  @media (max-width: 991px) {
    background-position: top;
  }
`;

const HeroSection = styled.div`
  width: 100%;
  min-height: 50vh;

  @media (max-width: 991px) {
    min-height: 35vh;
    margin-top: 80px;
    padding: 20vw 0px 20px;
  }
`;

const MainCardSection = styled.div`
  width: 100%;

  img {
    width: 100%;
    height: auto;
    margin: 0 auto;
    display: block;
  }
`;

const BottomSection = styled.div`
  margin-top: 2rem;

  .bottom-row {
    display: flex;
    gap: 10px;
    align-items: stretch;

    @media (max-width: 991px) {
      flex-direction: column;
    }
  }

  .p-0 {
    display: flex;
    flex: 1;
    min-width: 0;
  }
`;

const EventDetailsCard = styled.div`
  background-color: var(--black);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 2rem 1.5rem;
  text-align: left;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  h2 {
    color: var(--white);
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
    margin-bottom: 1.5rem;
    text-align: center;
    padding-bottom: 0.5rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 40%;
      height: 2px;
      background-color: var(--red);
    }
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);

  &:last-child {
    margin-bottom: 0;
  }

  .icon {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
  }

  .label {
    color: #ccc;
    font-size: 0.9rem;
    text-transform: uppercase;
    margin: 0;
    font-weight: 600;
  }

  .value {
    color: var(--white);
    font-size: 1rem;
    margin: 0;
  }
`;

const TicketPricingCard = styled.div`
  background-color: #000;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 2rem 1.5rem;
  text-align: center;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  h2 {
    color: var(--white);
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 40%;
      height: 2px;
      background-color: var(--red);
    }
  }

  .note {
    margin-top: 1.5rem;
    font-size: 1rem;
    color: #ccc;
    line-height: 1.5;

    p {
      margin: 0;
    }

    strong {
      color: var(--white);
    }

    em {
      font-style: italic;
    }
  }

  @media (max-width: 991px) {
    margin: 0.5rem 0;
  }
`;

const PricingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;

  .label {
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  .price {
    color: var(--red);
    font-size: 4rem;
    font-weight: bold;
    margin: 0;
    line-height: 1;
  }

  .separator {
    width: 2px;
    height: 80px;
    background-color: #666;
  }
  @media (max-width: 991px) {
    gap: 1rem;
  }
`;

const ImageWrapper = styled.div`
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  height: 100%;
  width: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 991px) {
    margin: 0.5rem 0;
  }
`;

export default FightQuest61;
